import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonProgressBar, IonIcon, IonButton, IonButtons, IonInfiniteScroll, IonInfiniteScrollContent, useIonModal } from '@ionic/react';
import { add } from 'ionicons/icons';
import AddDeviceModal from '../modals/AddDeviceModal';
import { useDevicesQuery } from '../query/devicesQuery';
import StateContainer, { LoadFailureStateContainer, LoadingStateContainer, NotLoggedInStateContainer } from '../components/StateContainer';
import { forwardRef, useCallback, useMemo } from 'react';
import { Virtuoso, type ListProps } from 'react-virtuoso';
import DeviceHeroCard from '../components/DeviceHeroCard';
import JiraTicketSearchModal from '../modals/JiraTicketSearchModal';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IonContentVirtuosoBottomInset, IonContentVirtuosoTopInset, ionContentVirtuosoStyle } from '../components/IonContentVirtuoso';

// Hero cards render their own card surface, so no list chrome is needed around them.
const DeviceList = forwardRef<HTMLDivElement, ListProps>((props, ref) => (
  <div ref={ref} {...props} />
));

const Devices: React.FC = () => {
  const { t } = useTranslation('pages');
  const devicesQuery = useDevicesQuery();
  const history = useHistory();

  const [presentAddModal, dismissAddModal] = useIonModal(AddDeviceModal, {
    dismiss: (data: string, role: string) => dismissAddModal(data, role)
  });

  const [presentJiraTicketSearchModal, dismissJiraTicketSearchModal] = useIonModal(JiraTicketSearchModal, {
    dismiss: (data: string, role: string) => {
      dismissJiraTicketSearchModal(data, role);

      if(role === 'navigate') {
        history.push(data);
      }
    }
  });

  const devices = useMemo(() => {
    return devicesQuery.data?.pages.reduce((acc, page) => acc.concat(page), []) ?? [];
  }, [devicesQuery.data]);

  const Footer = useCallback(() => {
    return (
      <>
        <IonInfiniteScroll onIonInfinite={async (event) => {
          await devicesQuery.fetchNextPage();
          await event.target.complete();
        }} disabled={!devicesQuery.hasNextPage} threshold="100px">
          <IonInfiniteScrollContent />
        </IonInfiniteScroll>
        <IonContentVirtuosoBottomInset />
      </>
    )
  }, [devicesQuery]);

  const currentUserQuery = useCurrentUserQuery();
  const isAdmin = currentUserQuery.data?.userLevel === 'ADMIN';

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('devices.title')}</IonTitle>
          <IonButtons slot="primary">
            <IonButton disabled={!devicesQuery.isSuccess} onClick={() => presentAddModal()}>
              <IonIcon slot="icon-only" icon={add} aria-label={t('devices.add_device')} />
            </IonButton>
            {isAdmin && <IonButton onClick={() => presentJiraTicketSearchModal({
              presentingElement: document.getElementsByTagName('ion-router-outlet')[0]
            })}>
              <IonIcon slot="icon-only" src="/assets/admin/find-ticket.svg" aria-label={t('devices.find_ticket')} />
            </IonButton>}
          </IonButtons>
        </IonToolbar>
        {devicesQuery.isLoading && !devicesQuery.isFetchingNextPage && <IonProgressBar type="indeterminate" />}
      </IonHeader>
      <IonContent fullscreen scrollY={false} color="light">
        {devicesQuery.isLoading && <LoadingStateContainer />}
        {devicesQuery.isError && ({
          401: <NotLoggedInStateContainer subtitle={t('devices.not_logged_in_subtitle')} />
        }[(devicesQuery.error as any).code as number] ?? <LoadFailureStateContainer subtitle={t('devices.couldnt_load_devices_subtitle')} error={devicesQuery.error} onRetry={() => devicesQuery.refetch()} />)}
        {devicesQuery.isSuccess && (devices.length === 0
          ? <StateContainer
              badge="add"
              title={t('devices.empty_title')}
              subtitle={t('devices.empty_subtitle')}
              actionLabel={t('devices.add_first_device')}
              onAction={() => presentAddModal()}
            />
          : <Virtuoso
              className="ion-content-scroll-host"
              style={ionContentVirtuosoStyle}
              data={devices}
              itemContent={(index, device) => <DeviceHeroCard deviceId={device.deviceId} />}
              components={{ Header: IonContentVirtuosoTopInset, Footer, List: DeviceList }}
              increaseViewportBy={{ top: 5, bottom: 5 }}
            />
        )}
      </IonContent>
    </IonPage >
  );
};

export default Devices;
